import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import { IconButton, Tooltip, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridRowId } from "@mui/x-data-grid";
import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router";
import { CommonService } from "../../Service/CommonService";
import { CookieService } from "../../Service/CookieService";
import { InventoryApiService } from "../../Service/InventoryApiService";
import "../Dynamic/InventoryCSV.scss";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import StatusDropdown from "../Common/StatusDropdown";
import EditIcon from '../../assets/images/icons/EditIcon.svg';
import TrashIcon from "../../assets/images/icons/Trash_Black.svg";
import Swal from "sweetalert2";
import DeleteItemIcon from "../../assets/images/icons/DeleteItem.png";
import { Form } from "react-bootstrap";
import { SupportApiService } from "../../Service/SupportApiService";
import ExpandMore from '../../assets/images/icons/ExpandMore.svg';
import Constant from "../../Constant";
import SupportFaqPanel from "./SupportFaqPanel";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DataGridLoader from "../Common/DataGridLoader";

interface LocationState {
    data: string;  // or whatever type your data is
    companyId: string;
}

const OwnershipTransferListing: React.FC = () => {
    const buttonRef = useRef<HTMLDivElement>(null);
    const divRef = useRef<HTMLDivElement>(null);
    const [showDropdown, setShowDropdown] = useState(false);
    const [viewFaq, setViewFaq] = useState(false);
    const toggleDropdown = () => { setShowDropdown((prev) => !prev); }
    const handleCloseFaq = () => setViewFaq(false);
    const handleShowFaq = () => setViewFaq(true);
    const location = useLocation();
    const currentPath = location.pathname;
    const [isLoading, setIsLoading] = useState(false);
    // Define the localeText object based on the isLoading flag
    const localeText = {
        noRowsLabel: isLoading ? "Fetching records ..." : "No Record Found",
    };
    const history = useHistory();
    const [search, setSearch] = useState("");
    const userRole = CookieService.getCookie("userRole");
    const { id } = useParams<{ id: string }>();
    const params = id === undefined && CommonService.isUser() ? CommonService.getCompanyId() : id;
    const [tableData, setTableData] = useState<any[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [sortField, setSortField] = useState("CreatedModifiedDate"); // Default sort
    const [sortDirection, setSortDirection] = useState("desc"); // Default ascending
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const initialFilter = {
        requestNumber: null,
        sourceCompany: null,
        destinationCompany: null,
        producer: null,
        lotNo: null,
        totalContainers: null,
        createdDate: null,
        createdBy: null,
        status: null,
        requestedOn: null
    };
    const [filter, setFilter] = useState<any>(initialFilter);
    const state = history.location.state as LocationState;
    const controllerRef = useRef<AbortController | null>(null);

    const statusOptions = [
        { value: "PENDING_REVIEW", label: "Pending Review" },
        { value: "UNDER_REVIEW", label: "Under Review" },
        { value: "ACCEPTED", label: "Accepted" },
        { value: "SOURCE_ACCEPTED", label: "Source Accepted" },
        { value: "DESTINATION_ACCEPTED", label: "Destination Accepted" },
        { value: "SOURCE_REJECTED", label: "Source Rejected" },
        { value: "DESTINATION_REJECTED", label: "Destination Rejected" },
        { value: "IN_PROGRESS", label: "In Progress" },
        { value: "OWNERSHIP_TRANSFERRED", label: "Ownership Transferred" },
        { value: "SEND_TO_WAREHOUSE", label: "Send to Warehouse" },
        { value: "IN_PROCESS", label: "In Process" },
        { value: "REASSIGN_TO_ADMIN", label: "Reassign to Admin" },
        { value: "WAREHOUSE_COMPLETED", label: "Warehouse Completed" },
        { value: "ADMIN_ACCEPTED_FOR_WAREHOUSE", label: "Admin Accepted for Warehouse" },
        { value: "RE_OPEN", label: "Re-open" },
        { value: "REJECTED", label: "Rejected" },
        { value: "CANCELLED", label: "Cancelled" },
        { value: "CLOSED", label: "Closed" },
        { value: "DELETED", label: "Deleted" }
    ];

    useEffect(() => {
        fetchOwnershipTransferListingData(page, pageSize, filter, sortField, sortDirection);
    }, []);

    const fetchOwnershipTransferListingData = async (page: any, size: any, filter: any, sortField: any, sortDirection: any) => {
        const controller = new AbortController();
        const signal = controller.signal;

        try {
            setIsLoading(true);
            setTableData([]);
            const responseListed: any = await InventoryApiService.getActiveOwnershipTransferListings(params, page, size, filter, sortField, sortDirection, signal);

            setTableData(responseListed.data.content);
            setTotalCount(responseListed.data.totalElements);
            setIsLoading(false);

        } catch (error) {
            console.error("Error fetching data:", error);
            setIsLoading(false);
        }
    };

    const handleRowClick = (params) => {
        const linkPrefix = CommonService.isAdmin() ? "/master" : "/user";
        history.push(`${linkPrefix}/view-customer-service-request/${params.row.id}`);
    }

    const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, field: keyof any) => {
        const value = event.target.value;

        const tempFilter = {
            ...filter,
            [field]: value,
        };

        setFilter(tempFilter);
        fetchOwnershipTransferListingData(page, pageSize, tempFilter, sortField, sortDirection);
    };

    //add tooltip
    const CellWithTooltip = ({ value }: { value: string | number }) => {
        return (
            <Tooltip arrow title={value?.toString()} sx={{ zIndex: 1 }} disableFocusListener disableTouchListener>
                <span>{value?.toString()}</span>
            </Tooltip>
        );
    };

    const handleStatusChange = (event: any, value: string, status: string | null) => {
        const isChecked = event.target.checked;

        // Convert the status string to an array
        const statusArray = status ? status.split(",") : [];

        // Update the status list based on checkbox state
        const updatedStatusArray = isChecked
            ? [...statusArray, value] // Add status if checked
            : statusArray.filter((s) => s !== value); // Remove status if unchecked

        // Convert array back to a comma-separated string
        const updatedStatus = updatedStatusArray?.length > 0 ? updatedStatusArray.join(",") : null;

        // Update the filter state with the modified status string
        setFilter({ ...filter, status: updatedStatus });

        // Reload service requests with updated filters
        fetchOwnershipTransferListingData(page, pageSize, { ...filter, status: updatedStatus }, sortField, sortDirection);
    };

    const OwnershipTransferListedColumns: GridColDef[] = [
        {
            field: "requestNumber",
            headerName: "Request Number",
            flex: 1,
            sortable: true,
            minWidth: 160,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader: (params) => (
                <div>
                    <Typography
                        variant="body2"
                        sx={{ color: "#6F6F6F", fontSize: "14px" }}
                    >
                        {params.colDef.headerName}
                    </Typography>
                    <div className="position-relative" style={{ width: "125px" }}>
                        <input
                            type="text"
                            className="form-control border mt-1"
                            style={{ textIndent: "24px", boxShadow: "none" }}
                            placeholder="Search"
                            defaultValue={filter.requestNumber}
                            onChange={(event) => handleFilterChange(event, "requestNumber")}
                            onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
                        />
                        <SearchIcon
                            sx={{
                                color: "#6F6F6F",
                                position: "absolute",
                                top: "50%",
                                left: "8px",
                                transform: "translateY(-50%)",
                            }}
                        />
                    </div>
                </div>
            ),
            renderCell: (params: any) => <CellWithTooltip value={params.value} />
        },
        {
            field: "sourceCompanyName",
            headerName: "Source Company",
            flex: 1,
            sortable: true,
            minWidth: 180,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader: (params) => (
                <div>
                    <Typography
                        variant="body2"
                        sx={{ color: "#6F6F6F", fontSize: "14px" }}
                    >
                        {params.colDef.headerName}
                    </Typography>
                    <div className="position-relative" style={{ width: "125px" }}>
                        <input
                            type="text"
                            className="form-control border mt-1"
                            style={{ textIndent: "24px", boxShadow: "none" }}
                            placeholder="Search"
                            defaultValue={filter.sourceCompanyName}
                            onChange={(event) => handleFilterChange(event, "sourceCompanyName")}
                            onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
                        />
                        <SearchIcon
                            sx={{
                                color: "#6F6F6F",
                                position: "absolute",
                                top: "50%",
                                left: "8px",
                                transform: "translateY(-50%)",
                            }}
                        />
                    </div>
                </div>
            ),
            renderCell: (params: any) => <CellWithTooltip value={params.value} />
        },
        {
            field: "destinationCompanyName",
            headerName: "Destination Company",
            flex: 1,
            sortable: true,
            minWidth: 180,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader: (params) => (
                <div>
                    <Typography
                        variant="body2"
                        sx={{ color: "#6F6F6F", fontSize: "14px" }}
                    >
                        {params.colDef.headerName}
                    </Typography>
                    <div className="position-relative" style={{ width: "125px" }}>
                        <input
                            type="text"
                            className="form-control border mt-1"
                            style={{ textIndent: "24px", boxShadow: "none" }}
                            placeholder="Search"
                            defaultValue={filter.destinationCompanyName}
                            onChange={(event) => handleFilterChange(event, "destinationCompanyName")}
                            onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
                        />
                        <SearchIcon
                            sx={{
                                color: "#6F6F6F",
                                position: "absolute",
                                top: "50%",
                                left: "8px",
                                transform: "translateY(-50%)",
                            }}
                        />
                    </div>
                </div>
            ),
            renderCell: (params: any) => <CellWithTooltip value={params.value || "-"} />
        },
        {
            field: "producer",
            headerName: "Producer",
            flex: 1,
            sortable: true,
            minWidth: 180,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader: (params) => (
                <div>
                    <Typography
                        variant="body2"
                        sx={{ color: "#6F6F6F", fontSize: "14px" }}
                    >
                        {params.colDef.headerName}
                    </Typography>
                    <div className="position-relative" style={{ width: "125px" }}>
                        <input
                            type="text"
                            className="form-control border mt-1"
                            style={{ textIndent: "24px", boxShadow: "none" }}
                            placeholder="Search"
                            defaultValue={filter.producer}
                            onChange={(event) => handleFilterChange(event, "producer")}
                            onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
                        />
                        <SearchIcon
                            sx={{
                                color: "#6F6F6F",
                                position: "absolute",
                                top: "50%",
                                left: "8px",
                                transform: "translateY(-50%)",
                            }}
                        />
                    </div>
                </div>
            ),
            renderCell: (params: any) => <CellWithTooltip value={params.value || "-"} />
        },
        {
            field: "lotNo",
            headerName: "LOT(s)",
            flex: 1,
            sortable: true,
            minWidth: 180,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader: (params) => (
                <div>
                    <Typography
                        variant="body2"
                        sx={{ color: "#6F6F6F", fontSize: "14px" }}
                    >
                        {params.colDef.headerName}
                    </Typography>
                    <div className="position-relative" style={{ width: "125px" }}>
                        <input
                            type="text"
                            className="form-control border mt-1"
                            style={{ textIndent: "24px", boxShadow: "none" }}
                            placeholder="Search"
                            defaultValue={filter.lotNo}
                            onChange={(event) => handleFilterChange(event, "lotNo")}
                            onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
                        />
                        <SearchIcon
                            sx={{
                                color: "#6F6F6F",
                                position: "absolute",
                                top: "50%",
                                left: "8px",
                                transform: "translateY(-50%)",
                            }}
                        />
                    </div>
                </div>
            ),
            renderCell: (params: any) => <CellWithTooltip value={params.value} />
        },
        {
            field: "totalContainers",
            headerName: "Total Containers",
            flex: 1,
            sortable: true,
            minWidth: 140,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader: (params) => (
                <div>
                    <Typography
                        variant="body2"
                        sx={{ color: "#6F6F6F", fontSize: "14px" }}
                    >
                        {params.colDef.headerName}
                    </Typography>
                    <div className="position-relative" style={{ width: "90px" }}>
                        <input
                            type="text"
                            className="form-control border mt-1"
                            style={{ textIndent: "24px", boxShadow: "none" }}
                            placeholder="Search"
                            defaultValue={filter.totalContainers}
                            onChange={(event) => handleFilterChange(event, "totalContainers")}
                            onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
                        />
                        <SearchIcon
                            sx={{
                                color: "#6F6F6F",
                                position: "absolute",
                                top: "50%",
                                left: "8px",
                                transform: "translateY(-50%)",
                            }}
                        />
                    </div>
                </div>
            ),
            renderCell: (params: any) => <CellWithTooltip value={params.value} />
        },
        {
            field: "createdDate",
            headerName: "Requested On",
            headerClassName: "table-header border-0 my-2 h-100",
            flex: 1,
            sortable: true,
            minWidth: 180,
            renderCell: (params: any) => <CellWithTooltip value={params.value} />,
            valueGetter: (value: any, row: any) => {
                const dateTimeString = row.createdDate ? row.createdDate : null;
                if (dateTimeString) {
                    return CommonService.formatDateTime(dateTimeString);
                }
            },
            renderHeader: (params) => (
                <div>
                    <Typography
                        variant="body2"
                        sx={{ color: "#6F6F6F", fontSize: "14px" }}
                    >
                        {params.colDef.headerName}
                    </Typography>
                    <input
                        type="date"
                        className="form-control border mt-1"
                        style={{ boxShadow: "none" }}
                        id="requestedOn"
                        name="requestedOn"
                        placeholder="MM/DD/YYYY"
                        defaultValue={filter.requestedOn}
                        onChange={(event) => handleFilterChange(event, "requestedOn")}
                        onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
                    />
                </div>
            ),
        },
        {
            field: "createdBy",
            headerName: "User",
            flex: 1,
            sortable: true,
            minWidth: 160,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader: (params) => (
                <div>
                    <Typography
                        variant="body2"
                        sx={{ color: "#6F6F6F", fontSize: "14px" }}
                    >
                        {params.colDef.headerName}
                    </Typography>
                    <div className="position-relative" style={{ width: "125px" }}>
                        <input
                            type="text"
                            className="form-control border mt-1"
                            style={{ textIndent: "24px", boxShadow: "none" }}
                            placeholder="Search"
                            defaultValue={filter.createdBy}
                            onChange={(event) => handleFilterChange(event, "createdBy")}
                            onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
                        />
                        <SearchIcon
                            sx={{
                                color: "#6F6F6F",
                                position: "absolute",
                                top: "50%",
                                left: "8px",
                                transform: "translateY(-50%)",
                            }}
                        />
                    </div>
                </div>
            ),
            renderCell: (params: any) => <CellWithTooltip value={params.value} />
        },
        {
            field: "status",
            headerName: "Status",
            headerClassName: "table-header border-0 my-2 h-100",
            minWidth: 280,
            cellClassName: "status-badge",
            flex: 1,
            sortable: true,
            renderCell: (params: any) => (
                <div
                    className="cell-with-dropdown mb-2"
                    onClick={(e) => e.stopPropagation()}>

                    <StatusDropdown
                        currentStatus={formatStatus(params.value)}
                        onStatusChange={(newStatus) => { }}
                        disableCondition={true}
                        allowedLabels={[]}
                    />
                    {(params.row.status === "REJECTED" || params.row.status === "CANCELLED") && params.row.rejectionNote && (
                        <Tooltip
                            title={`Rejection Note: ${params.row.rejectionNote}`}
                            arrow
                        >
                            <StickyNote2Icon
                                className="note-icon cursor-pointer"
                                style={{ color: "gray" }}
                            />
                        </Tooltip>
                    )}
                </div>
            ),
            renderHeader: (params) => (
                <>
                    <div>
                        <Typography
                            variant="body2"
                            sx={{ color: "#6F6F6F", fontSize: "14px" }}
                        >
                            {params.colDef.headerName}
                        </Typography>

                        <div className="position-relative" style={{ width: "210px" }}>
                            <div
                                className="form-control mb-1"
                                style={{
                                    cursor: "pointer",
                                    borderBottomColor: "#D1D1D1",
                                    marginTop: "4px",
                                    background: "#fff",
                                    padding: "5px",
                                    overflowX: "hidden",
                                    paddingRight: "16px",
                                    textOverflow: "ellipsis",
                                }}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    toggleDropdown();
                                }}
                                ref={buttonRef}
                            >
                                {filter.status?.length > 0 ? CommonService.formatStatus(filter.status).join(", ") : "Select Status"}
                                <img src={ExpandMore} className="position-absolute top-50 end-0 translate-middle"/>
                            </div>
                            {/* Custom Dropdown */}
                            {showDropdown && (
                                <div
                                    className="dropdown-menu show status-multiselect-dropdown"
                                    onClick={(event) => event.stopPropagation()}
                                    style={{whiteSpace:"wrap"}}
                                    ref={divRef}
                                >
                                    {statusOptions.map((option) => (
                                        <Form.Check
                                            key={option.value}
                                            id={`status-${option.value}`}
                                            type="checkbox"
                                            label={option.label}
                                            value={option.value}
                                            checked={filter.status?.split(",").includes(option.value) || false}
                                            onChange={(event) => {
                                                event.stopPropagation();
                                                handleStatusChange(event, option.value, filter.status);
                                            }}
                                            className="cursor-pointer"
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </>
            ),
        },
        {
            field: "action",
            headerName: "Action",
            sortable: false,
            minWidth: 90,
            flex: 1,
            headerClassName: "table-header border-0 my-2 h-100",
            renderCell: (params: any) => {
                const isSuperAdminAndUser =
                    params?.row?.roleName === "SUPER_ADMIN" && CommonService.isUser();

                const isReviewStage =
                    params?.row?.requestType !== Constant.CustomerService.TASTING &&
                    (params?.row?.status === Constant.CustomerServiceRequestStatus.PENDING_REVIEW ||
                        params?.row?.status === Constant.CustomerServiceRequestStatus.UNDER_REVIEW ||
                        params?.row?.status === Constant.CustomerServiceRequestStatus.SEND_TO_OWNER);

                const isTastingStage =
                    !CommonService.isUser() && params?.row?.requestType === Constant.CustomerService.TASTING &&
                    (params?.row?.status === Constant.CustomerServiceRequestStatus.SEND_TO_WAREHOUSE ||
                        params?.row?.status === Constant.CustomerServiceRequestStatus.ADMIN_ACCEPTED_FOR_WAREHOUSE);


                const isSellRequestAllowed =
                    params?.row?.requestType === Constant.CustomerService.SELL_REQUEST &&
                    !["CANCELLED", "REJECTED", "DELETED", "UNPUBLISHED"].includes(
                        params?.row?.status
                    );

                const isWantedToBuyAllowed =
                    params?.row?.requestType === Constant.CustomerService.WANTED_TO_BUY &&
                    !["CANCELLED", "REJECTED", "DELETED", "COMPLETED"].includes(
                        params?.row?.status
                    );

                const isCustomSampleAllowed =
                    params?.row?.requestType === Constant.CustomerService.SAMPLE_REQUEST &&
                    params?.row?.status === Constant.CustomerServiceRequestStatus.SEND_TO_OWNER;

                return (
                    <>
                        {(((params?.row?.requestType == Constant.CustomerService.SAMPLE_REQUEST || params?.row?.requestType !== Constant.CustomerService.TASTING ||
                            params?.row?.requestType == Constant.CustomerService.CUSTOM_SAMPLE_REQUEST) &&
                            ['PENDING_REVIEW', 'UNDER_REVIEW', 'SEND_TO_OWNER'].includes(params?.row?.status)) ||
                            ((!isSuperAdminAndUser || params?.row?.requestType == Constant.CustomerService.RE_CONTAINERIZATION ||
                                params?.row?.requestType == Constant.CustomerService.BARREL_HEAD_PAINTING ||
                                params?.row?.requestType !== Constant.CustomerService.TASTING) && isReviewStage || isTastingStage) || isSellRequestAllowed || isWantedToBuyAllowed) && (
                                <div
                                    className="d-flex align-items-center gap-2 h-100"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {/* Edit Button */}
                                    <Tooltip title="Edit" arrow>
                                        <IconButton
                                            aria-label="edit"
                                            size="small"
                                            onClick={() => handleEditServiceRequest(params?.row?.id)}
                                            disabled={
                                                !(params?.row?.requestType == Constant.CustomerService.SAMPLE_REQUEST || params?.row?.requestType == Constant.CustomerService.CUSTOM_SAMPLE_REQUEST) &&
                                                params?.row?.requestType !== Constant.CustomerService.SELL_REQUEST && params?.row?.requestType !== Constant.CustomerService.WANTED_TO_BUY &&
                                                (isSuperAdminAndUser && params?.row?.requestType != Constant.CustomerService.RE_CONTAINERIZATION && params?.row?.requestType != Constant.CustomerService.BARREL_HEAD_PAINTING && params?.row?.requestType != Constant.CustomerService.TASTING) &&
                                                !isCustomSampleAllowed // allow edit when custom sample + send_to_owner
                                            }
                                        >
                                            <img
                                                src={EditIcon}
                                                className="cursor-pointer"
                                                alt="EditIcon"
                                            />
                                        </IconButton>
                                    </Tooltip>

                                    {/* Delete Button (only for certain status/roles) */}
                                    {(((params?.row?.requestType == Constant.CustomerService.SAMPLE_REQUEST || params?.row?.requestType == Constant.CustomerService.CUSTOM_SAMPLE_REQUEST) || (!isSuperAdminAndUser)) && ['PENDING_REVIEW', 'UNDER_REVIEW', 'SEND_TO_OWNER'].includes(params?.row?.status)) && (
                                        <Tooltip title="Delete" arrow>
                                            <IconButton
                                                aria-label="delete"
                                                size="small"
                                                onClick={() => deleteServiceRequest(params?.row?.id)}
                                                disabled={!(params?.row?.requestType == Constant.CustomerService.SAMPLE_REQUEST || params?.row?.requestType == Constant.CustomerService.CUSTOM_SAMPLE_REQUEST) && isSuperAdminAndUser}
                                            >
                                                <img
                                                    src={TrashIcon}
                                                    height={22}
                                                    width={22}
                                                    alt="DeleteIcon"
                                                />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </div>
                            )}
                    </>
                );
            },
        },
    ].filter(Boolean) as GridColDef[];

    function formatStatus(status: string): string {
        const formattedStatus = status
            .split(/[_\\]/).map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(" ");
        return formattedStatus;
    }

    const handleEditServiceRequest = (id: any) => {
        const linkPrefix = CommonService.isAdmin() ? "/master" : "/user";
        history.push(`${linkPrefix}/edit-customer-service-request/${id}`);
    }

    const deleteServiceRequest = async (id: any) => {
        Swal.fire({
            html: `
            <style>
                .custom-swal .icon-container {
                    background-color: #ffebee;
                    border-radius: 50%;
                    width: 105px;
                    height: 105px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    margin: 0 auto 20px;
                }
                .custom-swal .icon-container i {
                    color: #ff3e4e;
                    font-size: 24px;
                }
                .custom-swal h2 {
                    font-size: 20px;
                    margin-bottom: 10px;
                    margin-top: 20px;
                }
                .custom-swal p {
                    font-size: 14px;
                    color: #666;
                    margin-bottom: 20px;
                }
                .custom-swal .button-container {
                    margin-top: 40px;
                    display: flex;
                    justify-content: center;
                    gap: 20px;
                }
                .custom-swal button {
                    border: none;
                    padding: 10px 50px;
                    border-radius: 30px;
                    font-size: 14px;
                    cursor: pointer;
                }
                .custom-swal .cancel-btn {
                    background-color: #e0e0e0;
                    color: #333;
                }
                .custom-swal .delete-btn {
                    background-color: #ec2323;
                    color: white;
                }
                
            </style>
            <div class="custom-swal">
        
            <div class="icon-container" id="iconContainer">
                </div>
        
                <h2>Are you sure?</h2>
                <p>Remove service request</p>
                <div class="button-container">
                <button class="cancel-btn" id="cancelBtn">Cancel</button>
                <button class="delete-btn" id="deleteBtn">Remove</button>
                </div>
            </div>
            `,
            showConfirmButton: false,
            showCancelButton: false,
            showCloseButton: true,
            focusCancel: true,
            customClass: {
                popup: "custom-popup",
            },
            backdrop: `rgba(0,0,0,0.4)`,
            width: "500px",
            background: "#f8f8f8",
            didOpen: () => {
                // Add the image after the popup has been rendered
                const iconContainer = Swal.getPopup()!.querySelector("#iconContainer");
                if (iconContainer) {
                    const img = document.createElement("img");
                    img.src = DeleteItemIcon;
                    img.width = 47;
                    img.height = 51;
                    iconContainer.appendChild(img);
                }

                // Add event listeners to the buttons
                const cancelBtn = Swal.getPopup()!.querySelector("#cancelBtn");
                const deleteBtn = Swal.getPopup()!.querySelector("#deleteBtn");
                cancelBtn?.addEventListener("click", () => { Swal.close() });
                deleteBtn?.addEventListener("click", async () => {

                    // Remove the item from the cart locally
                    try {
                        const response = await SupportApiService.deleteCustomerServiceRequest(id);

                        // After removing the service request, show this message
                        setTimeout(() => {
                            CommonService.Toast.fire({
                                title: "Service request removed successfully",
                                icon: "success",
                            });

                        }, 1000);
                        fetchOwnershipTransferListingData(page, pageSize, filter, sortField, sortDirection);
                    } catch (error) {
                        console.error("Error deleting service request:", error);
                    }

                    Swal.close();
                });
            },
        });
    }

    // Map filtered rows with unique IDs
    const tableDataWithIds = tableData.map((rowData, index) => ({
        ...rowData,
    }));

    const handleBack = () => {
        if (currentPath.startsWith("/master/marketplace-listings")) {
            history.push(`/master/dashboard`);
        } else {
            history.goBack();
        }

    };

    const onPaginationModelChange = (newPaginationModel) => {
        setPage(newPaginationModel.page);
        setPageSize(newPaginationModel.pageSize);

        // Fetch using the latest sorting values
        fetchOwnershipTransferListingData(newPaginationModel.page, newPaginationModel.pageSize, filter, sortField, sortDirection);
    };

    const handleSortChange = (sortModel) => {
        if (sortModel.length > 0) {
            const newSortField = sortModel[0].field;
            const newSortDirection = sortModel[0].sort; // "asc" or "desc" directly from DataGrid

            setSortField(newSortField);
            setSortDirection(newSortDirection);

            fetchOwnershipTransferListingData(page, pageSize, filter, newSortField, newSortDirection);
        }
    };

    const handleAddRequest = () => {
        if (CommonService.isAdmin())
            history.push("/master/add-customer-service-request", {
                data: state?.data,
                companyId: state?.companyId
            });
        else
            history.push("/user/add-customer-service-request", { data: state?.data, });
    };

    const exportMyOwnershipTransfer = async (page: any, size: any, filter: any, sortField: any, sortDirection: any) => {
        const controller = new AbortController();
        const signal = controller.signal;

        try {
            setIsLoading(true);

            // Cancel the previous request if any
            if (controllerRef.current) {
                controllerRef.current.abort();
            }

            // Store the current controller to cancel the request if needed
            controllerRef.current = controller;
            InventoryApiService.exportMyOwnershipTransfer(filter, sortField, sortDirection, signal);
            CommonService.Toast.fire({
                title: "Your export is in progress. We'll notify you when it's ready!",
                icon: "success",
            });
            setIsLoading(false);
        } catch (error) {
            console.error("Error fetching data:", error);
            setIsLoading(false);
        }
    };

    return (
        <div className={`usersList ${CommonService.isAdmin() ? 'adminInventoryList' : 'inventoryList'}`} style={{ paddingBottom: CommonService.isAdmin() ? 53 : "" }}>
            <div className="usersList__header--wrapper d-flex flex-wrap justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2 left-wrapper">
                    {CommonService.isAdmin() && (
                        <IconButton
                            onClick={handleBack}
                            aria-label="ArrowBackIcon"
                            sx={{
                                backgroundColor: "#FFF",
                                "&:hover": { backgroundColor: "#FFF" },
                            }}
                        >
                            <ArrowBackIcon sx={{ color: "#1C1B1F" }} />
                        </IconButton>
                    )}
                    <div className="header--title me-2">
                        <h1 className="title mb-0">
                            Ownership Transfer
                            <i className="bi bi-heart-fill"></i>
                        </h1>
                    </div>
                    <div className="divider mx-2"></div>
                    {tableData.length > 0 || isLoading ?
                        <>
                        <button title="Export to Excel"
                            className="primary-btn d-inline-flex align-items-center me-1"
                            style={{ paddingTop: "4px", paddingLeft: 11 }}
                            onClick={(e) => {
                            e.currentTarget.blur(); // Removes focus after clicking
                            exportMyOwnershipTransfer(page, pageSize, filter, sortField, sortDirection);
                            }}
                        >
                            <FileDownloadOutlinedIcon sx={{ marginTop: "5.5px" }} />
                            Export
                        </button>
                        </>
                    : null}
                    <button
                        className="primary-btn d-inline-flex"
                        onClick={handleAddRequest}
                    >
                        <AddSharpIcon sx={{ mr: 0.5 }} />
                        Submit Request
                    </button>
                </div>
                <div className="ms-auto d-flex gap-2 align-items-center">
                    <div>
                        <button className="support-faq-btn" onClick={handleShowFaq}>
                            Click here for FAQs and Tutorials
                        </button>
                    </div>
                </div>
            </div>
            <div className="user-table-container position-relative">
                {viewFaq && <SupportFaqPanel handleCloseFaq={handleCloseFaq} />}
                <div className="userTable">
                    <DataGrid
                        rows={tableDataWithIds}
                        columns={OwnershipTransferListedColumns}
                        className="data-grid support-req--listing"
                        paginationModel={{ page, pageSize }}
                        pagination
                        paginationMode="server"
                        sortingMode="server"
                        rowCount={totalCount}
                        onSortModelChange={handleSortChange}
                        onPaginationModelChange={onPaginationModelChange}
                        disableRowSelectionOnClick={true}
                        loading={isLoading}
                        slots={{
                            loadingOverlay: DataGridLoader,
                        }}
                        pageSizeOptions={[10, 20, 50, 100]}    
                        onRowClick={handleRowClick}
                        disableColumnResize
                        getRowHeight={() => 52}
                    />
                </div>
            </div>
        </div>
    );
};

export default OwnershipTransferListing;